import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {formatDate} from "../../utils/helper";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { statusColor } from "../../assets/utils/statusColors";

const PaymentRequestsList = () => {
    const [items, setItems] = useState([]);
    const [isLoader, setIsLoader] = useState(false);
    const [pagination, setPagination] = useState({});
    const [currency] = useState(process.env.REACT_APP_CURRENCY || 'AED');

    const [apiData, setApiData] = useState({
        search: "",
        daterange: "",
        status: "",
        page: 1,
    });
     

    const fetchData = async () => {
        setIsLoader(true);
        try {
            const { data } = await ApiService.request({
                method: 'GET',
                url: '/payment-requests',
                data: apiData
            });
            if (data.status) {
                setItems(data.data);
                setPagination(data.pagination);
            }
        } finally {
            setIsLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [apiData.page, apiData.search, apiData.daterange, apiData.status]);

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({
            search: '',
            daterange: '',
            status: '',
            page: 1
        });
    };

    const approveRequest = async (id) => {
        const result = await Swal.fire({
            title: 'Approve Payment Request?',
            text: 'Are you sure you want to approve this payment request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Yes, approve it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
            input: 'textarea',
            inputPlaceholder: 'Add admin notes (optional)',
            inputAttributes: {
                'aria-label': 'Add admin notes'
            },
            showCancelButton: true
        });

        if (result.isConfirmed) {
            try {
                const response = await ApiService.request({
                    method: 'POST',
                    url: `/payment-requests/${id}/approve`,
                    data: { admin_notes: result.value || '' }
                });
                const data = response.data;
                if (data.status) {
                    fetchData();
                    toast.success(data.message);
                } else {
                    toast.error(data.message);
                }
            } catch (error) {
                toast.error('Error approving payment request');
            }
        }
    };

    const markAsPaid = async (id) => {
        const result = await Swal.fire({
            title: 'Mark as Paid?',
            text: 'This will deduct the amount from user wallet. Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, mark as paid!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
            input: 'textarea',
            inputPlaceholder: 'Add admin notes (optional)',
            inputAttributes: {
                'aria-label': 'Add admin notes'
            },
            showCancelButton: true
        });

        if (result.isConfirmed) {
            try {
                const response = await ApiService.request({
                    method: 'POST',
                    url: `/payment-requests/${id}/mark-paid`,
                    data: { admin_notes: result.value || '' }
                });
                const data = response.data;
                if (data.status) {
                    fetchData();
                    toast.success(data.message);
                } else {
                    toast.error(data.message);
                }
            } catch (error) {
                toast.error('Error marking payment request as paid');
            }
        }
    };

    const rejectRequest = async (id) => {
        const result = await Swal.fire({
            title: 'Reject Payment Request?',
            text: 'Are you sure you want to reject this payment request?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, reject it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
            input: 'textarea',
            inputPlaceholder: 'Add rejection reason (optional)',
            inputAttributes: {
                'aria-label': 'Add rejection reason'
            },
            showCancelButton: true
        });

        if (result.isConfirmed) {
            try {
                const response = await ApiService.request({
                    method: 'POST',
                    url: `/payment-requests/${id}/reject`,
                    data: { admin_notes: result.value || '' }
                });
                const data = response.data;
                if (data.status) {
                    fetchData();
                    toast.success(data.message);
                } else {
                    toast.error(data.message);
                }
            } catch (error) {
                toast.error('Error rejecting payment request');
            }
        }
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'Pending': 'warning',
            'Approved': 'info',
            'Paid': 'success',
            'Rejected': 'danger'
        };
        return statusColors[status] || 'secondary';
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Payment Requests</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Payment Requests</li>
                        </ul>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className="d-flex align-items-center position-relative my-1">
                                                <input 
                                                    type="text" 
                                                    value={apiData.search} 
                                                    onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })} 
                                                    className="form-control form-control-solid me-5"
                                                    placeholder="Search by code, user name, email..." 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <select 
                                                className="form-select form-select-solid"
                                                value={apiData.status}
                                                onChange={e => setApiData({ ...apiData, status: e.target.value, page: 1 })}
                                            >
                                                <option value="">All Status</option>
                                                <option value="Pending">Pending</option>
                                                <option value="Approved">Approved</option>
                                                <option value="Paid">Paid</option>
                                                <option value="Rejected">Rejected</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <DatePicker
                                                selectsRange={true}
                                                startDate={apiData.daterange[0]}
                                                endDate={apiData.daterange[1]}
                                                onChange={e => setApiData({ ...apiData, daterange: e, page: 1 })} 
                                                isClearable={true}
                                                className='form-control form-control-solid'
                                                placeholderText="Select Date Range"
                                            />
                                        </div>
                                        <div className="col-md-3 mt-2">
                                            <button 
                                                type="button" 
                                                onClick={clearFilter} 
                                                className={`btn btn-sm btn-secondary mx-1 ${!apiData.search && !apiData.daterange && !apiData.status ? 'd-none' : ''}`}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div id="kt_payment_requests_table_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>Request Code</th>
                                                <th>User</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Notes</th>
                                                <th>Created Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {items.length > 0 ? items.map((item, index) => (
                                                <tr key={item._id}>
                                                    <td>{item.request_code || '-'}</td>
                                                    <td>
                                                        {item.user_id ? (
                                                            <div>
                                                                <div className="fw-bold">{item.user_id.name || '-'}</div>
                                                                <div className="text-muted small">{item.user_id.email || '-'}</div>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td>{currency} {item.amount?.toFixed(2) || '0.00'}</td>
                                                    <td>
                                                        <span className={`badge bg-${getStatusBadge(item.status)}`}>
                                                            {item.status || 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '200px' }} title={item.notes || item.admin_notes || '-'}>
                                                            {item.notes || item.admin_notes || '-'}
                                                        </div>
                                                    </td>
                                                    <td>{formatDate(item.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            {item.status === 'Pending' && (
                                                                <>
                                                                    <button
                                                                        onClick={() => approveRequest(item._id)}
                                                                        className="btn btn-sm btn-success"
                                                                        title="Approve"
                                                                    >
                                                                        <i className="bi bi-check-circle"></i>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => rejectRequest(item._id)}
                                                                        className="btn btn-sm btn-danger"
                                                                        title="Reject"
                                                                    >
                                                                        <i className="bi bi-x-circle"></i>
                                                                    </button>
                                                                </>
                                                            )}
                                                            {item.status === 'Approved' && (
                                                                <button
                                                                    onClick={() => markAsPaid(item._id)}
                                                                    className="btn btn-sm btn-primary"
                                                                    title="Mark as Paid"
                                                                >
                                                                    <i className="bi bi-currency-dollar"></i> Mark Paid
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-10">
                                                        <div className="text-muted">No payment requests found</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table> 
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center"  />
                                    )}
                                    <span>Total Records: {pagination?.total_records || 0}</span>
                                    {pagination && pagination.total_pages > 1 && (
                                        <Paginator
                                            currentPage={pagination.current_page}
                                            totalPages={pagination.total_pages}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentRequestsList;



